import dayjs, { type Dayjs } from 'dayjs'
import lodash from 'lodash'
import { ErrorAumentado } from '@comun/errorAumentado'
import { AlmacenamientoTemporal } from './datos-persistentes.client'

// Implementations
import { viaNotiWorker } from '../lib/notificaciones/web'
import { ConectorNativo } from '../lib/notificaciones/nativas'
import type { InterfazNotificaciones } from '../lib/notificaciones/interface'
import type { IdentificadorActivo, NotificacionAumentada, NotificacionEnApp, NotificacionData, TiposDeNotificacionEnApp } from '@comun/tipos/notificaciones'
import { NotificacionAumentadaZod, DataNotificacionEnAppZod } from '@comun/tipos/notificaciones'
import { LlaverosAPI } from './llavero-cliente'
import { EventosDeAppAPI } from './eventosDeApp'

// ==========================================
// 1. Notificaciones En App
// ==========================================

const notificacionesEnAppRef = ref<Record<string, NotificacionEnApp>>({})
const notificacionDefaults: Partial<NotificacionEnApp> = {
  centrado: false,
  cerrable: true,
  cerrada: false,
  duracionSegundos: 7,
  iconoMedida: '3em'
}

function MiniID(): string {
  return Math.random().toString(36).substring(2, 9)
}

async function GenerarNotificacionEnApp(nuevaNotificacion: NotificacionEnApp) {
  const snap = unref(notificacionesEnAppRef)
  const notificacionID = nuevaNotificacion.id
  snap[notificacionID] = nuevaNotificacion
  notificacionesEnAppRef.value = snap
}

export const NotificacionesEnApp = {
  lista: computed(() => unref(notificacionesEnAppRef)),

  info: lodash.throttle(async function (data: NotificacionData) {
    const id = `info-${MiniID()}`
    const g: NotificacionEnApp = Object.assign(
      {
        ...notificacionDefaults,
        id,
        tipo: 'info' as TiposDeNotificacionEnApp,
        icono: 'solar:info-square-line-duotone',
        creacion: dayjs(),
        duracionSegundos: 3
      },
      data
    )
    return GenerarNotificacionEnApp(g)
  }, 1000, { trailing: false }),

  exito: lodash.throttle(async function (data: NotificacionData) {
    const id = `exito-${MiniID()}`
    const g: NotificacionEnApp = Object.assign(
      {
        ...notificacionDefaults,
        id,
        tipo: 'exito' as TiposDeNotificacionEnApp,
        icono: 'solar:check-circle-line-duotone',
        creacion: dayjs(),
        duracionSegundos: 5
      },
      data
    )
    return GenerarNotificacionEnApp(g)
  }, 1000, { trailing: false }),

  atencion: lodash.throttle(async function (data: NotificacionData) {
    const id = `atencion-${MiniID()}`
    const g: NotificacionEnApp = Object.assign(
      {
        ...notificacionDefaults,
        id,
        tipo: 'atencion' as TiposDeNotificacionEnApp,
        icono: 'solar:danger-triangle-line-duotone',
        creacion: dayjs(),
        duracionSegundos: 7
      },
      data
    )
    return GenerarNotificacionEnApp(g)
  }, 1000, { trailing: false }),

  error: lodash.throttle(async function (data: NotificacionData) {
    const id = `error-${MiniID()}`
    const g: NotificacionEnApp = Object.assign(
      {
        ...notificacionDefaults,
        id,
        tipo: 'error' as TiposDeNotificacionEnApp,
        icono: 'solar:danger-circle-line-duotone',
        creacion: dayjs(),
        duracionSegundos: 5
      },
      data
    )
    return GenerarNotificacionEnApp(g)
  }, 1000, { trailing: false }),

  cerrar: async (notificacionID: string) => {
    const snap = unref(notificacionesEnAppRef)
    const notificacion = snap[notificacionID]
    if (!notificacion) return
    notificacion.cerrada = true
    snap[notificacionID] = notificacion
    notificacionesEnAppRef.value = snap
  },

  cerrarTodas: async () => {
    const snap = unref(notificacionesEnAppRef)
    for (const notificacionID in snap) {
      if (snap[notificacionID]) snap[notificacionID].cerrada = true
    }
    notificacionesEnAppRef.value = snap
  }
}

// ==========================================
// Configuración Compartida (Adapter)
// ==========================================

const isWeb = contextoApp.buildConfig.dispositivo === 'web'
const adaptador: InterfazNotificaciones = isWeb ? viaNotiWorker : ConectorNativo
const permisoConcedidoEnOS = ref<boolean>(false)
const estadoPermisoOS = ref<'granted' | 'denied' | 'prompt'>('prompt')
const activadasEnApp = ref<boolean>(false)
const inicializado = ref<boolean>(false)
const suscripcionBrowserCreada = ref<boolean>(false)
const suscripcionRegistradaEnBackend = ref<boolean>(false)

const soporteWebPush = computed(() => {
  if (!isWeb) return true
  return typeof window !== 'undefined' && [
    'Notification' in window,
    'serviceWorker' in navigator,
    'PushManager' in window
  ].every(Boolean)
})

// Timeout helper para operaciones que pueden colgarse
async function conTimeout<T>(promesa: Promise<T>, ms: number, label: string): Promise<T> {
  const timeout = new Promise<never>((_, reject) => 
    setTimeout(() => reject(new Error(`${label} timeout (${ms}ms)`)), ms)
  )
  return Promise.race([promesa, timeout])
}

/**
 * Crea la suscripción push en el browser (ServiceWorker/PushManager)
 * Esta función SOLO crea la suscripción local, NO la registra en el servidor
 * Retorna la suscripción creada o null si falla
 */
async function crearSuscripcionPush(): Promise<PushSubscription | null> {
  if (!permisoConcedidoEnOS.value) {
    consolo.warn('[notificaciones]', 'No se puede crear suscripción sin permisos')
    return null
  }

  if (!isWeb) {
    // En nativo, la suscripción se maneja diferente via Capacitor
    consolo.log('[notificaciones]', 'crearSuscripcionPush: nativo - no requiere suscripción browser')
    suscripcionBrowserCreada.value = true
    return null
  }

  try {
    consolo.log('[notificaciones]', 'Creando suscripción push en browser...')
    const registration = await navigator.serviceWorker.ready
    
    const applicationServerKey = unref(contextoActualizadoApp)?.appConfig?.applicationServerKey
    if (!applicationServerKey) {
      consolo.error('[notificaciones]', 'No hay applicationServerKey configurada')
      return null
    }

    let subscription = await registration.pushManager.getSubscription()
    
    if (!subscription) {
      consolo.log('[notificaciones]', 'Creando nueva suscripción push...')
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })
    } else {
      consolo.log('[notificaciones]', 'Usando suscripción push existente')
    }

    suscripcionBrowserCreada.value = true
    consolo.log('[notificaciones]', '✅ Suscripción browser creada/verificada')
    return subscription
  } catch (error) {
    consolo.error('[notificaciones]', '❌ Error creando suscripción push', error)
    return null
  }
}

/**
 * Wrapper centralizado de suscripción con deduplicación
 * Llama al adaptador (web.ts o nativas.ts) que implementa Lista Única
 */
async function suscribirViaAdaptador(): Promise<void> {
  await adaptador.suscribir()
}

async function verificarSuscripcionExistente(): Promise<boolean> {
  if (!isWeb) return false

  try {
    // Timeout de 3 segundos para evitar bloqueo si SW no responde
    const registration = await conTimeout(
      navigator.serviceWorker.ready, 
      3000, 
      'serviceWorker.ready'
    )
    const subscription = await registration.pushManager.getSubscription()
    const existe = !!subscription
    
    // Actualizar estado local si existe
    if (existe) suscripcionBrowserCreada.value = true
    
    return existe
  } catch (error) {
    consolo.warn('[notificaciones] Error verificando suscripción existente:', error)
    return false
  }
}

let intentosInicializacion = 0
const MAX_INTENTOS = 3

async function initShared() {
   
  console.log('[notificaciones] initShared llamado, inicializado.value:', inicializado.value)
  if (inicializado.value) {
     
    console.log('[notificaciones] initShared: ya inicializado, omitiendo')
    return
  }
  
  intentosInicializacion++
   
  console.log(`[notificaciones] initShared: 🚀 INICIO inicialización (intento ${intentosInicializacion}/${MAX_INTENTOS})`)
  consolo.log('[notificaciones]', 'Notificaciones>init')

  try {
    // // eslint-disable-next-line no-console
    // console.log('[notificaciones]', 'initShared', 'Llamando adaptador.leerPermiso()...')
    // Leer permiso del OS
    const permisoActual = await adaptador.leerPermiso()
    permisoConcedidoEnOS.value = permisoActual

    const estadoActual = await adaptador.obtenerEstadoPermiso()
    estadoPermisoOS.value = estadoActual

    consolo.info('[notificaciones]', 'Permiso inicial:', permisoActual, 'Estado:', estadoActual)

    // Verificar si hay suscripción browser existente (solo informativo)
    const yaEstaSuscritoBrowser = await verificarSuscripcionExistente()
    consolo.info('[notificaciones]', 'Suscripción browser existente:', yaEstaSuscritoBrowser)

    // NO suscribir aquí - los watchers de permiso se encargarán (con o sin usuario)

    adaptador.on('cambioPermiso', async (data: unknown) => {
      const permitido = data as boolean
      consolo.info('[notificaciones] cambioPermiso', { permitido })

      permisoConcedidoEnOS.value = permitido
      
      const estadoActual = await adaptador.obtenerEstadoPermiso()
      estadoPermisoOS.value = estadoActual

      if (!permitido) {
        suscripcionBrowserCreada.value = false
        suscripcionRegistradaEnBackend.value = false
      }
      // La suscripción la maneja el watcher de permisoConcedidoEnOS
    })

    // Watcher del permiso: dispara suscripción cuando se conceden permisos
    // Ya NO requiere usuario logueado - las notificaciones funcionan sin usuario
    watch(permisoConcedidoEnOS, async (permitido) => {
      consolo.log('[notificaciones] watcher permiso', {
        permitido,
        browserCreada: suscripcionBrowserCreada.value,
        backendRegistrada: suscripcionRegistradaEnBackend.value,
        isWeb
      })

      if (!permitido) {
        consolo.log('[notificaciones] Permiso revocado o no concedido, omitiendo')
        return
      }

      // Si ya está todo registrado, no hacer nada
      if (suscripcionBrowserCreada.value && suscripcionRegistradaEnBackend.value) {
        consolo.log('[notificaciones] Suscripción ya completa (watcher permiso), omitiendo')
        return
      }

      consolo.log('[notificaciones] Permiso concedido, iniciando suscripción...')

      // Suscribir via adaptador (web o nativo) - deduplicación en el adaptador
      await suscribirViaAdaptador()
    }, { immediate: true })

    // Watcher opcional del usuario: si hay cambios en usuario logueado,
    // solo re-registrar si ya tenemos suscripción pero cambió el usuario
    // (para actualizar asociación en backend si es necesario)
    watch(usuario, async (usr) => {
      consolo.log('[notificaciones] watcher usuario (cambio de sesión)', {
        tieneUsuario: !!usr,
        permiso: permisoConcedidoEnOS.value,
        browserCreada: suscripcionBrowserCreada.value,
        backendRegistrada: suscripcionRegistradaEnBackend.value
      })

      // Si hay usuario, permisos concedidos, y ya estábamos suscritos,
      // podríamos querer re-registrar para asociar al nuevo usuario
      // Esto es opcional y depende de la lógica de negocio
      if (usr && permisoConcedidoEnOS.value && suscripcionBrowserCreada.value) {
        consolo.log('[notificaciones] Usuario logueado con suscripción existente - considerar re-registro')
      }
    }, { immediate: true })

    adaptador.on('notificacionRecibida', async (data: unknown) => {
      consolo.log('[notificaciones]', 'notificacionRecibida raw urlQueAbrir:', (data as any)?.urlQueAbrir)
      const resultado = NotificacionAumentadaZod.safeParse(data)
      if (!resultado.success) {
        consolo.warn('[notificaciones]', 'notificacionRecibida: formato inválido', resultado.error)
        return
      }
      
      const notificacion = resultado.data
      consolo.log('[notificaciones]', 'notificacionRecibida parsed urlQueAbrir:', notificacion.urlQueAbrir)
      
      // 🚨 VERIFICACIÓN DE TERMINAL ID: Asegurar que la notificación sea para este terminal
      const terminalIDEnNotificacion = notificacion.data?.terminalIDDestino as string | undefined
      if (terminalIDEnNotificacion) {
        // Obtener el llavero actual para comparar
        try {
          const llaveros = await LlaverosAPI.ObtenerLlaveros()
          const terminalIDActual = llaveros.llaveroCliente.llaveroID
          
          if (terminalIDEnNotificacion !== terminalIDActual) {
            consolo.warn('[notificaciones]', `⚠️ Notificación recibida para otro terminal (${terminalIDEnNotificacion.substring(0, 15)}...) - ignorando y reportando al servidor`)
            
            // Reportar al backend que este terminal está obsoleto
            try {
              const headers = await HeadersConAuth()
              await comoSiFueraPorAxios({
                url: `${apiUrl}/notificador/reportarTerminalObsoleto`,
                method: 'post',
                headers,
                data: {
                  terminalIDRecibido: terminalIDEnNotificacion,
                  terminalIDActual,
                  notificacionID: notificacion.id
                }
              })
              consolo.log('[notificaciones]', '✅ Terminal obsoleto reportado al servidor')
            } catch (error) {
              consolo.error('[notificaciones]', '❌ Error reportando terminal obsoleto:', error)
            }
            
            return // No mostrar la notificación
          }
        } catch (error) {
          consolo.error('[notificaciones]', 'Error obteniendo llavero para verificación:', error)
          // Si no podemos verificar, mostrar la notificación de todos modos (fail-open)
        }
      }
      
      if (notificacion.data?.silenciosa === true) {
        consolo.log('[notificaciones]', 'Notificación silenciosa recibida — emitiendo AppAPI.notificacionRecibida sin UI')
        AppAPI.emit('notificacionRecibida', notificacion)
        return
      }

      if (notificacion.data?.solicitarActualizarApp === true) {
        consolo.log('[notificaciones]', 'solicitarActualizarApp recibido — emitiendo notificacion:modalRemoto')
        EventosDeAppAPI.emitirEventoDeApp('notificacion:modalRemoto', notificacion)
        return
      }

      NotificacionesEnApp.info(notificacion)
    })

    adaptador.on('notificationClick', (data: unknown) => {
      consolo.log('[notificaciones]', 'notificationClick', data)
      const resultado = NotificacionAumentadaZod.safeParse(data)
      if (!resultado.success) return
      const notificacion = resultado.data
      if (notificacion.data?.solicitarActualizarApp === true || notificacion.urlQueAbrir) {
        consolo.log('[notificaciones]', 'notificationClick — emitiendo notificacion:modalRemoto')
        EventosDeAppAPI.emitirEventoDeApp('notificacion:modalRemoto', notificacion)
        return
      }
    })

    adaptador.on('notificationClose', (data: unknown) => {
      consolo.log('[notificaciones]', 'notificationClose', data)
    })

    // Timeout de 5 segundos para adaptador.init()
    await conTimeout(adaptador.init(), 5000, 'adaptador.init()')

    // Re-leer permiso post-init
    permisoConcedidoEnOS.value = await conTimeout(
      adaptador.leerPermiso(), 
      3000, 
      'leerPermiso'
    )
    estadoPermisoOS.value = await conTimeout(
      adaptador.obtenerEstadoPermiso(),
      3000,
      'obtenerEstadoPermiso'
    )
    inicializado.value = true
     
    console.log('[notificaciones] initShared: ✅ Inicialización completada, permiso:', permisoConcedidoEnOS.value, 'Estado:', estadoPermisoOS.value)
    consolo.log('[notificaciones]', 'Inicialización completada')
  } catch (error) {
     
    console.error('[notificaciones] initShared: ❌ Error en init:', error)
    consolo.error('[notificaciones]', 'Error en init', error)
    
    // Reintentar hasta MAX_INTENTOS con backoff exponencial
    if (intentosInicializacion < MAX_INTENTOS) {
      const delay = Math.pow(2, intentosInicializacion) * 1000 // 2s, 4s, 8s
      consolo.log(`[notificaciones] Reintentando inicialización en ${delay}ms...`)
      setTimeout(() => {
        inicializado.value = false // Resetear para permitir reintento
        initShared()
      }, delay)
    } else {
       
      console.error(`[notificaciones] initShared: Agotados ${MAX_INTENTOS} intentos, marcando como inicializado con error`)
      inicializado.value = true // Evitar reintentos infinitos después de MAX_INTENTOS
    }
  }
}

// Escuchar rotación de llavero para invalidar suscripción y forzar re-registro
EventosDeAppAPI.capturarEventoDeApp('llavero:rotado', (origen) => {
  consolo.log('[notificaciones] Llavero rotado desde', origen, '— invalidando suscripción push para re-registro')
  suscripcionRegistradaEnBackend.value = false
  if (isWeb) {
    suscripcionBrowserCreada.value = false
  }
})

// ==========================================
// 2. Notificaciones Locales
// ==========================================

export const NotificacionesLocales = {
  async programar(notificacion: NotificacionAumentada) {
    await initShared()
    const ahora = dayjs()
    if (dayjs(notificacion.fechaEnQueMostrar).isBefore(ahora)) {
      notificacion.fechaEnQueMostrar = ahora.add(2, 's').toDate()
    }
    await adaptador.programar(notificacion)
    consolo.log(`Notificación programada para ${dayjs(notificacion.fechaEnQueMostrar).format('DD MMM HH:mm')}`)
  },

  async programarEstas(notificaciones: NotificacionAumentada[]) {
    await initShared()
    await adaptador.programarSoloEstasNotificaciones(notificaciones)
  },

  async cancelarTodas() {
    await initShared()
    await adaptador.cancelarTodas()
  }
}

// ==========================================
// 3. Notificaciones Push
// ==========================================

const apiUrl = contextoApp.buildConfig.apiUrl
const notificablesRef = ref<Record<string, IdentificadorActivo>>({})

export const NotificacionesPush = {
  // Estado
  inicializado: computed(() => inicializado.value),
  permitidasEnSistema: computed(() => permisoConcedidoEnOS.value),
  estadoPermisoOS: computed(() => estadoPermisoOS.value),
  activadasEnApp: computed(() => activadasEnApp.value),
  
  // Estados de suscripción separados
  suscrito: computed(() => suscripcionRegistradaEnBackend.value), // Compatibilidad: suscrito = registrado en backend
  browserCreada: computed(() => suscripcionBrowserCreada.value),
  backendRegistrada: computed(() => suscripcionRegistradaEnBackend.value),
  
  incapaz: computed(() => {
    if (contextoApp.buildConfig.dispositivo !== 'web') return false
    return !unref(soporteWebPush)
  }),
  aptoParaWebPush: soporteWebPush,
  destinatariosConocidos: computed(() => notificablesRef.value),

  // Gestión de suscripción
  async init() {
    activadasEnApp.value = (await AlmacenamientoTemporal.leer<string>('pushActivo')) === 'si'
    return initShared()
  },

  async reiniciar() {
     
    console.log('[NotificacionesPush] Reinicio manual solicitado')
    intentosInicializacion = 0
    inicializado.value = false
    return initShared()
  },

  async pedirPermiso() {
    const resultado = await adaptador.pedirPermiso()
    permisoConcedidoEnOS.value = resultado
    return resultado
  },

  async configurarActivacion(activa: boolean) {
    const fx = 'NotificacionesPush>configurarActivacion'
    activadasEnApp.value = activa
    AlmacenamientoTemporal.guardar('pushActivo', activa ? 'si' : 'no')

    try {
      const headers = await HeadersConAuth()
      await comoSiFueraPorAxios({
        url: `${apiUrl}/notificador/preferencias`,
        method: 'post',
        headers,
        data: { preferencias: { activadas: activa } }
      })
      consolo.log(fx, 'Preferencias sincronizadas', { activadas: activa })
    } catch (error) {
      consolo.error(fx, 'Error sincronizando preferencias', error)
    }
  },

  // Métodos de suscripción
  async suscribir() {
    return suscribirViaAdaptador()
  },

  async asegurarSuscripcion() {
    return suscribirViaAdaptador()
  },

  // Emisor / API (Backend interaction)
  Emisor: {
    async probarPush({
      notificacion,
      usuarioIDs
    }: { notificacion: NotificacionAumentada, usuarioIDs: string[] }) {
      const fx = 'probarPush'
      try {
        consolo.log(fx)
        // Asegurar que esté suscrito antes de enviar
        await NotificacionesPush.asegurarSuscripcion()

        const headers = await HeadersConAuth()
        // Omitir el campo 'id' ya que el backend lo genera automáticamente
        const { id, ...notificacionSinID } = notificacion

        // IMPORTANTE: Agregar fechaDeEnvio al data para que coincida con el servidor
        // El servidor agrega este campo en el backend (líneas 389 y 439 de notificacionesPush.ts)
        const notificacionConFechaDeEnvio = {
          ...notificacionSinID,
          data: {
            ...notificacionSinID.data,
            fechaDeEnvio: new Date().toISOString()
          }
        }

        const dataToSend = { notificacion: notificacionConFechaDeEnvio, usuarioIDs }
        return await comoSiFueraPorAxios({
          url: `${apiUrl}/notificador/probar`,
          method: 'post',
          headers,
          data: dataToSend
        })
      } catch (e) {
        throw e instanceof ErrorAumentado ? e.trazar(fx) : new ErrorAumentado(fx, { error: e })
      }
    },

    async enviarNotificaciones(
      notificacion: NotificacionAumentada,
      usuarioIDs: string[],
      opciones?: { mismoTerminal?: boolean }
    ) {
      const fx = 'enviarNotificaciones'
      try {
        // Asegurar que esté suscrito antes de enviar
        await NotificacionesPush.asegurarSuscripcion()

        const headers = await HeadersConAuth()
        // Omitir el campo 'id' ya que el backend lo genera automáticamente
        const { id, ...notificacionSinID } = notificacion

        // IMPORTANTE: Agregar fechaDeEnvio al data para que coincida con el servidor
        // El servidor agrega este campo en el backend (líneas 389 y 439 de notificacionesPush.ts)
        const notificacionConFechaDeEnvio = {
          ...notificacionSinID,
          data: {
            ...notificacionSinID.data,
            fechaDeEnvio: new Date().toISOString()
          }
        }

        return await comoSiFueraPorAxios({
          url: `${apiUrl}/notificador/enviar`,
          method: 'post',
          headers,
          data: { notificacion: notificacionConFechaDeEnvio, usuarioIDs, mismoTerminal: opciones?.mismoTerminal }
        })
      } catch (e) {
        throw e instanceof ErrorAumentado ? e.trazar(fx) : new ErrorAumentado(fx, { error: e })
      }
    }
  }
}
